import { createContext, useContext } from 'react';

import type { AdminUser } from '@/types/admin';

/**
 * Whether anyone is signed in.
 *
 * `unknown` is the state the app boots into and matters more than it looks:
 * until `GET /admin/auth/me` answers, showing either the login form or the
 * panel would be a guess, and both guesses flash the wrong screen at an
 * operator who reloads.
 */
export type AuthStatus = 'unknown' | 'authed' | 'anonymous';

export interface AuthValue {
  status: AuthStatus;
  user: AdminUser | null;
  /** Why they are at the door, when they did not arrive there by choice. */
  reason: string | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /** Re-read the account, without blanking the screen. */
  refresh(): Promise<void>;
  /** Synchronous, no network: what the 401 handler calls. */
  expire(reason: string): void;
}

export const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside the admin routes.');
  }
  return value;
}
